import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { GridSelectedPollworkerRow } from 'admin/src/components/Pollworker/types';
import type {
  CustomField,
  CustomFieldData,
  Party,
  PollworkerApiResponse,
  PollworkerUserInfo,
  PollworkerWorkAssignmentConfirmationsForElection,
  PollworkerWorkHistory,
  Precinct,
  RoleInfo,
  Schedule,
} from 'admin/src/types';

export type GridState = {
  parties: Party[];
  precincts: Precinct[];
  roleTypes: RoleInfo[];
  selectedRows: GridSelectedPollworkerRow[];
  selectedRowsPollworkerIds: string[];
  selectedVotingLocationId?: string;
  pollworkers: PollworkerApiResponse;
  allWorkers: PollworkerWorkHistory[];
  reportsTo: PollworkerUserInfo[];
  customFields: CustomField[];
  pollworkerCustomFieldsData: CustomFieldData[];
  pollworkerWorkAssignmentConfirmationsForElection: PollworkerWorkAssignmentConfirmationsForElection[];
  quickFilter: string | undefined;
  pollworkerToEdit?: { pollworker: PollworkerWorkHistory; column: string };
  showPollworkerDetailsId?: string;
  displayedRowCount: number;
  isFiltered: boolean;
  loadingPollworkerData: boolean;
  isEditingPollWorker: boolean;
  isBulkEditActive: boolean;
  scrollTo?: string;
};

const defaultGridReducer: GridState = {
  parties: [],
  precincts: [],
  roleTypes: [],
  selectedRows: [],
  allWorkers: [],
  selectedRowsPollworkerIds: [],
  selectedVotingLocationId: undefined,
  pollworkers: { workerList: [], precinctList: [], locationList: [] },
  reportsTo: [],
  customFields: [],
  pollworkerCustomFieldsData: [],
  pollworkerWorkAssignmentConfirmationsForElection: [],
  quickFilter: undefined,
  pollworkerToEdit: undefined,
  showPollworkerDetailsId: undefined,
  displayedRowCount: 0,
  isFiltered: false,
  loadingPollworkerData: false,
  isEditingPollWorker: false,
  isBulkEditActive: false,
};

const gridSlice = createSlice({
  name: 'grid',
  initialState: defaultGridReducer,
  reducers: {
    setShowPollworkerDetailsId: (state, action) => {
      state.showPollworkerDetailsId = action.payload;
    },
    setPollworkerToEdit: (state, action) => {
      state.pollworkerToEdit = action.payload;
    },
    updatePollworkerLocation: (state, action: PayloadAction<Schedule>) => {
      const locIndex = state.pollworkers.locationList.findIndex((loc) => loc.id === action.payload.id);
      if (locIndex > -1) state.pollworkers.locationList[locIndex] = action.payload;
    },
    updateAllPollworkerLocations: (state, action: PayloadAction<Schedule[]>) => {
      state.pollworkers.locationList = action.payload;
    },
    updatePollworker: (state, action: PayloadAction<PollworkerWorkHistory>) => {
      const idx = state.pollworkers.workerList.findIndex((history: PollworkerWorkHistory) => history.id === action.payload.id);
      if (idx > -1) state.pollworkers.workerList[idx] = action.payload;
    },
    setParties: (state, action: PayloadAction<Party[]>) => {
      state.parties = action.payload;
    },
    setPrecincts: (state, action: PayloadAction<Precinct[]>) => {
      state.precincts = action.payload;
    },
    setSelectedRowsPollworkerIds: (state, action: PayloadAction<string[]>) => {
      state.selectedRowsPollworkerIds = action.payload;
    },
    setSelectedVotingLocation: (state, action: PayloadAction<string>) => {
      state.selectedVotingLocationId = action.payload;
    },
    setReportsTo: (state, action: PayloadAction<PollworkerUserInfo[]>) => {
      state.reportsTo = action.payload;
    },
    setCustomFields: (state: GridState, action: PayloadAction<CustomField[]>) => {
      state.customFields = action.payload;
    },
    setPollworkerCustomFieldsData: (state, action: PayloadAction<CustomFieldData[]>) => {
      state.pollworkerCustomFieldsData = action.payload;
    },
    setPollworkerWorkAssignmentConfirmationsForElection: (state, action: PayloadAction<PollworkerWorkAssignmentConfirmationsForElection[]>) => {
      state.pollworkerWorkAssignmentConfirmationsForElection = action.payload;
    },
    clearSelectedRows: (state, action) => {
      state.selectedRowsPollworkerIds = [];
    },
    setPollworkers: (state, action: PayloadAction<PollworkerApiResponse>) => {
      state.pollworkers = action.payload;
    },
    setRoleTypes: (state, action: PayloadAction<RoleInfo[]>) => {
      state.roleTypes = action.payload;
    },
    setQuickFilter: (state, action) => {
      state.quickFilter = action.payload;
    },
    setDisplayedRowCount: (state, action) => {
      state.displayedRowCount = action.payload;
    },
    setIsFiltered: (state, action) => {
      state.isFiltered = action.payload;
    },
    setLoadingPollworkerData(state, action) {
      state.loadingPollworkerData = action.payload;
    },
    setIsEditingPollWorker(state, action) {
      state.isEditingPollWorker = action.payload;
      if (action.payload) {
        state.isBulkEditActive = false;
      }
    },
    setBulkEditActive(state, action: PayloadAction<boolean>) {
      state.isBulkEditActive = action.payload;
      if (action.payload) {
        state.isEditingPollWorker = false;
      }
    },
    fetchPollworkersStart: (state) => {},
    fetchPollworkersSuccess: (
      state,
      action: PayloadAction<{
        pollworkers: PollworkerApiResponse;
        parties: Party[];
        roleTypes: RoleInfo[];
        customFields: CustomField[];
        pollworkerCustomFieldsData: CustomFieldData[];
        pollworkerWorkAssignmentConfirmationsForElection: PollworkerWorkAssignmentConfirmationsForElection[];
        reportsTo: PollworkerUserInfo[];
      }>,
    ) => {
      state.pollworkers = action.payload.pollworkers;
      state.parties = action.payload.parties;
      state.roleTypes = action.payload.roleTypes;
      state.customFields = action.payload.customFields;
      state.pollworkerCustomFieldsData = action.payload.pollworkerCustomFieldsData;
      state.pollworkerWorkAssignmentConfirmationsForElection = action.payload.pollworkerWorkAssignmentConfirmationsForElection;
      state.reportsTo = action.payload.reportsTo;
      state.loadingPollworkerData = false;
    },
    fetchPollworkersFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.loadingPollworkerData = false;
    },
    fetchAllWorkersStart: (state) => {},
    fetchAllWorkersSuccess: (
      state,
      action: PayloadAction<{
        workers: PollworkerWorkHistory[];
        parties: Party[];
        roleTypes: RoleInfo[];
        customFields: CustomField[];
        pollworkerCustomFieldsData: CustomFieldData[];
      }>,
    ) => {
      state.allWorkers = action.payload.workers;
      state.parties = action.payload.parties;
      state.roleTypes = action.payload.roleTypes;
      state.customFields = action.payload.customFields;
      state.pollworkerCustomFieldsData = action.payload.pollworkerCustomFieldsData;
      state.loadingPollworkerData = false;
    },
    fetchAllWorkersFailure: (state, action: PayloadAction<{ error: string }>) => {
      state.loadingPollworkerData = false;
    },
    setScrollTo: (state, action: PayloadAction<string | undefined>) => {
      state.scrollTo = action.payload;
    },
  },
});

export const {
  setCustomFields,
  updatePollworker,
  setParties,
  setPrecincts,
  setSelectedRowsPollworkerIds,
  setSelectedVotingLocation,
  setReportsTo,
  setPollworkerCustomFieldsData,
  setPollworkerWorkAssignmentConfirmationsForElection,
  setPollworkers,
  setRoleTypes,
  setQuickFilter,
  setPollworkerToEdit,
  updatePollworkerLocation,
  updateAllPollworkerLocations,
  setShowPollworkerDetailsId,
  clearSelectedRows,
  setDisplayedRowCount,
  setIsFiltered,
  setLoadingPollworkerData,
  setIsEditingPollWorker,
  setBulkEditActive,
  fetchPollworkersStart,
  fetchPollworkersSuccess,
  fetchPollworkersFailure,
  fetchAllWorkersStart,
  fetchAllWorkersSuccess,
  fetchAllWorkersFailure,
  setScrollTo,
} = gridSlice.actions;

export default gridSlice.reducer;
